import type { AlgorithmResult, AlgorithmResultEntry, Observation, PanelPosition } from "@allergy123/contracts/clinical";
import { Button, Card, Status } from "@allergy123/ui";
import { useMemo, useState } from "react";
import { MAX_COMPONENTS, selectable, toggle, violations, type Candidate } from "../lib/selection";
import { GROUP_LABELS, paperRows } from "../lib/tray";

type Mode = "suggested" | "manual" | "current";

/** The patient's current treatment: the components of the most recent submitted order on an earlier test. */
export interface CurrentTreatment {
  encounterId: string;
  treatmentYear: number;
  components: Array<{ antigenId: string; name: string }>;
}

/**
 * The results screen (inventory.md 4.6, C-42): the five suggested antigens, then every well in the paper's column
 * pairs; Suggested or Manual select; the selection rules applied as the user taps. "Continue current" waits on a
 * prior-order lookup (Task 3.6).
 */
export function ResultsView({ result, positions, observations, selection, onSelection, readOnly = false, chosen, current }: { result: AlgorithmResult; positions: PanelPosition[]; observations: Observation[]; selection: string[]; onSelection: (ids: string[]) => void; readOnly?: boolean; chosen?: AlgorithmResultEntry[]; current?: CurrentTreatment | null }) {
  const [mode, setMode] = useState<Mode>(() => (selection.length && !sameSet(selection, suggestedIds(result)) ? "manual" : "suggested"));
  const readings = useMemo(() => new Map(observations.map((o) => [o.antigenId, o.value])), [observations]);
  const candidates = useMemo(() => new Map<string, Candidate>(positions.map((p) => [p.antigenId, { antigenId: p.antigenId, position: p.position, reading: readings.get(p.antigenId) ?? null, isControl: p.controlKind !== null }])), [positions, readings]);
  const entries = useMemo(() => new Map([...result.suggested, ...result.other].filter((e) => e.antigenId).map((e) => [e.antigenId!, e])), [result]);
  const rows = useMemo(() => paperRows(positions), [positions]);
  const problems = violations(selection, candidates);

  const useSuggested = () => {
    setMode("suggested");
    onSelection(suggestedIds(result));
  };
  /** Maps the prior order onto this test: components that reacted this time are kept, the rest are named as dropped. */
  const carried = current ? current.components.map((c) => ({ ...c, keep: (() => { const cand = candidates.get(c.antigenId); return !!cand && !cand.isControl && cand.reading !== null && cand.reading > 0; })() })) : [];
  const useCurrent = () => {
    setMode("current");
    onSelection(carried.filter((c) => c.keep).map((c) => c.antigenId).slice(0, MAX_COMPONENTS));
  };
  const pick = (antigenId: string) => {
    if (readOnly) return;
    const c = candidates.get(antigenId);
    if (!c) return;
    if (mode === "suggested") setMode("manual");
    onSelection(toggle(selection, c, candidates));
  };

  return (
    <div className="flex flex-col gap-6">
      {!result.controls.valid && <Status kind="warning" label={`Controls: ${controlsText(result)}. The result is shown; the clinician decides.`} />}
      {result.versionNote && <Status kind="info" label={result.versionNote} />}
      <Card as="section" aria-labelledby="suggested" dense>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h3 id="suggested" className="text-h3 font-semibold text-anchor">
            Suggested treatment
          </h3>
          <span className="text-small text-ink-secondary">
            Histamine {result.testA ?? "—"} mm · saline {result.testB ?? "—"} mm · ruleset {result.rulesetId}
          </span>
        </div>
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {result.suggested.map((e, i) => (
            <li key={`${e.antigenId ?? "abs"}-${i}`} className={`rounded-md border p-3 ${e.abs ? "border-rule-subtle text-ink-secondary" : "border-rule-strong"}`}>
              <p className="text-small text-ink-secondary">#{i + 1}</p>
              <p className="font-medium">{e.abs ? "Open slot" : e.name}</p>
              {!e.abs && (
                <p className="text-small text-ink-secondary">
                  {e.reaction} mm · score {e.calcVal ?? "—"}
                  {e.season && ` · ${e.season}`}
                </p>
              )}
              {factorText(e) && <p className="text-small text-ink-secondary">{factorText(e)}</p>}
            </li>
          ))}
        </ol>
        {result.absSlots > 0 && <p className="mt-3 text-small text-ink-secondary">{result.absSlots} of five slots had no qualifying antigen.</p>}
      </Card>

      {chosen && chosen.length > 0 && (
        <Card dense>
          <h3 className="mb-2 text-h3 font-semibold text-anchor">Ordered</h3>
          <p>{chosen.map((c) => c.name).join(", ")}</p>
        </Card>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-small font-medium text-ink">Selection</span>
          <Button variant={mode === "suggested" ? "primary" : "secondary"} onClick={useSuggested}>
            Suggested
          </Button>
          <Button variant={mode === "manual" ? "primary" : "secondary"} onClick={() => setMode("manual")}>
            Manual select
          </Button>
          {current && (
            <Button variant={mode === "current" ? "primary" : "secondary"} onClick={useCurrent}>
              Continue current
            </Button>
          )}
          <span className="text-small text-ink-secondary">
            {selection.length} of {MAX_COMPONENTS} picked
          </span>
        </div>
      )}
      {!readOnly && mode === "current" && current && <Status kind="info" label={`Current treatment (year ${current.treatmentYear}): ${current.components.map((c) => c.name).join(", ")}.${carried.some((c) => !c.keep) ? ` Not carried forward (no reaction this time): ${carried.filter((c) => !c.keep).map((c) => c.name).join(", ")}.` : ""}`} />}
      {!readOnly && problems.length > 0 && selection.length > 0 && <Status kind="warning" label={problems.join(" ")} />}

      <section aria-label="All wells, in paper order" className="flex flex-col gap-4">
        {rows.map((row, i) => (
          <div key={i} className="grid gap-4 md:grid-cols-2">
            {[row.left, row.right].map((g) => (
              <Card key={g.group} dense>
                <h4 className="mb-2 text-small font-medium text-ink-secondary">{GROUP_LABELS[g.group]}</h4>
                <ul className="flex flex-col gap-1">
                  {g.items.map((p) => {
                    const c = candidates.get(p.antigenId)!;
                    const e = entries.get(p.antigenId);
                    const picked = selection.includes(p.antigenId);
                    const can = selectable(c, selection);
                    const why = c.isControl ? "control" : c.reading === null ? "not read" : c.reading <= 0 ? "no reaction" : null;
                    return (
                      <li key={p.antigenId}>
                        <button
                          type="button"
                          disabled={readOnly || (!picked && !can.ok)}
                          aria-pressed={picked}
                          onClick={() => pick(p.antigenId)}
                          className={`grid w-full grid-cols-[2rem_1fr_auto] items-center gap-2 rounded-sm px-2 py-1 text-left text-body disabled:text-ink-secondary ${picked ? "bg-selection font-semibold" : "hover:bg-card-tinted"}`}
                        >
                          <span className="text-small text-ink-secondary">{p.position}</span>
                          <span className="truncate">
                            {p.displayName}
                            {why && <span className="ml-1 text-small text-ink-secondary">({why})</span>}
                          </span>
                          <span className="tabular-nums text-small text-ink-secondary">
                            {c.reading ?? "—"} mm{e?.calcVal != null ? ` · ${e.calcVal}` : ""}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}

export const suggestedIds = (result: AlgorithmResult): string[] => result.suggested.filter((e) => !e.abs && e.antigenId).map((e) => e.antigenId!);
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
const CONTROL_REASONS: Record<string, string> = { saline_reacted: "the saline control reacted", histamine_absent: "the histamine control did not react", saline_reacted_and_histamine_absent: "the saline control reacted and histamine did not" };
const controlsText = (r: AlgorithmResult) => CONTROL_REASONS[r.controls.reason ?? ""] ?? "not valid";
/** The scoring factors as the legacy presenter labelled them, non-empty ones only. */
const factorText = (e: AlgorithmResultEntry) => Object.values(e.factors ?? {}).map((f) => (f as { label?: string } | null)?.label ?? "").filter(Boolean).join(" · ");
